import React, { useEffect, useState, useCallback, useMemo } from "react";
import PopupWrapper from "../popupWrapper/popupWrapper";
import { popupTexts } from "../../utils/constants";
import { Select, MenuItem, FormHelperText } from "@mui/material";
import "./rejectPopup.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRejectStatusesAsync,
  updateOrderStatusAsync,
} from "../../redux/reducers/orderStatus";
import { useParams } from "react-router-dom";
import { ReactComponent as DropdownIcon } from "../../assets/icons/arrow-down.svg";
import { fetchOrderDetailsAsync } from "../../redux/reducers/customerAgentDetailsDashboard";

const RejectPopup = ({ onClose, status, setToast }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [reason, setReason] = useState("");
  const { orderStatusData } = useSelector((state) => state.orderStatus);
  const [rejectError, setRejectError] = useState(false);
  const [otherReasonInput, setOtherReasonInput] = useState("");
  const [showOtherReason, setShowOtherReason] = useState(false);
  const [inputError, setInputError] = useState("");
  const customerInfo = useMemo(() => {
    return JSON.parse(localStorage.getItem("customerInfo"));
  }, []);

  const updatedCustomerInfo = useMemo(
    () => ({
      ...customerInfo,
      status: reason,
    }),
    [customerInfo, reason]
  );

  const reasons = useMemo(
    () =>
      (status || []).map((item) => ({
        label: item.name,
        value: item.code,
      })),
    [status]
  );

  const handleReasonChange = useCallback((event) => {
    setReason(event.target.value);
    setRejectError(false);
    setInputError("");

    if (event.target.value === "REJECTED_OTHER") {
      setShowOtherReason(true);
    } else {
      setShowOtherReason(false);
    }
  }, []);

  const handleOtherReasonChange = (e) => {
    e.preventDefault();
    const value = e.target.value;
    setOtherReasonInput(value);
    if (value.length < 5) {
      setInputError("Minimum 5 characters required");
    } else if (value.length > 250) {
      setInputError("Maximum 250 characters exceeded");
    } else if (value.trim() === "") {
      setInputError("Please enter a reason");
    } else {
      setInputError("");
    }
  };

  const handleReject = useCallback(() => {
    if (reason === "REJECTED_OTHER" && otherReasonInput.trim().length === 0) {
      setInputError("Please enter a reason");
      return;
    }

    const payload = { orderId: id, status: reason };

    if (reason === "REJECTED_OTHER") {
      payload.reject_reason = otherReasonInput.trim();
    }

    if (reason || inputError) {
      dispatch(updateOrderStatusAsync(payload)).then((res) => {
        if (res?.payload) {
          setToast({ header: res?.payload?.message });
          dispatch(fetchOrderDetailsAsync(id));
        }
      });
    } else {
      setRejectError(true);
    }
  }, [dispatch, id, reason, otherReasonInput]);

  useEffect(() => {
    if (
      orderStatusData?.customer_support_agent_info &&
      orderStatusData?.reseller_agent_info
    ) {
      localStorage.setItem("customerInfo", JSON.stringify(updatedCustomerInfo));
      onClose();
    }
  }, [orderStatusData, updatedCustomerInfo, onClose]);

  useEffect(() => {
    dispatch(fetchRejectStatusesAsync("REJECTED"));
  }, [dispatch]);

  return (
    <PopupWrapper onClose={onClose}>
      <div className="reject-popup">
        <p className="reject-popup-header">{popupTexts.reject_header}</p>
        <div className="reject-popup-actions">
          <Select
            className="reject-popup-cancel"
            value={reason}
            onChange={handleReasonChange}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <span className="select-placeholder">Select reason</span>
                );
              }
              const reasonObj = reasons.find((r) => r.value === selected);
              return reasonObj ? reasonObj.label : selected;
            }}
            IconComponent={(props) => (
              <DropdownIcon {...props} className="reject-dropdown-icon" />
            )}
          >
            {reasons.map((reason) => (
              <MenuItem
                sx={{ borderRadius: "6px" }}
                key={reason.value}
                value={reason.value}
              >
                {reason.label}
              </MenuItem>
            ))}
          </Select>
          {rejectError && <p className="error reject-error">Select a reason</p>}
          {showOtherReason && (
            <>
              <textarea
                className="other-reason-textarea"
                placeholder="Enter your reason (5-250 characters)"
                value={otherReasonInput}
                onChange={handleOtherReasonChange}
                maxLength={250}
                res
              />
              {inputError && (
                <p className="error other-reason-error">{inputError}</p>
              )}
            </>
          )}
          <button
            className="reject-popup-reject"
            onClick={handleReject}
            disabled={inputError}
          >
            {popupTexts.reject_btn}
          </button>
        </div>
      </div>
    </PopupWrapper>
  );
};

export default RejectPopup;
